import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import * as Yup from 'yup';
import {
  DetailContext,
  DetailHandle,
  DetailToolbarButton2,
  DetailToolbarButtonProps,
  FormTextField,
  PromptContext,
  useEventCallback,
  usePrompts,
} from '@eas/common-web';
import { CourtFile, DictSettlementMethod, PlenaryOpinionFile } from '@models';
import { Agenda } from '@enums';

export function SaveButton<T extends CourtFile | PlenaryOpinionFile>(
  props: DetailToolbarButtonProps
) {
  const { testPrompt } = useContext(PromptContext);
  const { source, formRef } = useContext<DetailHandle<T>>(DetailContext);

  const [deletedMethods, setDeletedMethods] = useState<DictSettlementMethod[]>(
    []
  );

  const label = deletedMethods.map((d) => d.label).join(', ');

  usePrompts([
    {
      key: 'COURT_FILE_ASSIGNMENT_CHANGE_REASON',
      dialogTitle: 'Důvod změny',
      dialogText: 'Uveďte důvod změny soudce nebo rozhodujícího orgánu.',
      FormFields: function FormFields() {
        return (
          <FormTextField
            name="reason"
            label="Důvod změny"
            required
            multiline
          />
        );
      },
      formValidationSchema: Yup.object().shape({
        reason: Yup.string().trim().required('Povinné pole'),
      }),
    },
  ]);

  usePrompts(
    [
      {
        key: 'WARNING',
        dialogTitle: (
          <FormattedMessage id="ES__WARNING__TITLE" defaultMessage="Varování" />
        ),
        dialogText: (
          <FormattedMessage
            id="ES__WARNING__TEXT"
            defaultMessage="Smazali jste způsoby vyřízení: {label} Po uložení budou tyto způsoby vyřízení smazány ze všech rozhodnutí."
            values={{
              label: (
                <>
                  <br />
                  {deletedMethods.map((method) => (
                    <React.Fragment key={method.id}>
                      <b>{method?.label}</b>
                      <br />
                    </React.Fragment>
                  ))}

                  <br />
                </>
              ),
            }}
          />
        ),
      },
    ],
    [label]
  );

  const submit = useEventCallback((values?: { reason?: string }) => {
    const reason = values?.reason?.trim();
    if (reason) {
      formRef?.setFieldValue(
        'changeReason',
        `Důvod změny soudce / rozhodujícího orgánu: ${reason}`
      );
    }
    if (deletedMethods.length) {
      setTimeout(
        () =>
          testPrompt({
            key: 'WARNING',
            submitCallback: () => {
              setTimeout(() => props.onClick(), 0);
            },
          }),
        0
      );
    } else {
      setTimeout(() => props.onClick(), 0);
    }
  });

  const handleSave = useEventCallback(() => {
    const decisionSettlementMethods =
      source.data?.decisions
        ?.map((d) => d.settlementMethods)
        .flat()
        .map((d) => d!.id) ?? [];

    const originalMethods = source.data?.settlementMethods ?? [];
    const newMethods = formRef?.getFieldValues().settlementMethods ?? [];

    const deletedMethods = originalMethods.filter(
      (d) => !newMethods.map((d) => d.id)?.includes(d.id)
    );

    const deletedImportantMethods = deletedMethods?.filter((d) =>
      decisionSettlementMethods.includes(d.id)
    );

    setDeletedMethods(deletedImportantMethods);

    const values = formRef?.getFieldValues() as any;
    const initial = source.data as any;
    const judgeChanged =
      initial?.agenda === Agenda.COURT &&
      initial?.judge?.id !== values?.judge?.id;
    const members = (membersValue: any[]) =>
      (membersValue ?? [])
        .map((member) => member?.id)
        .filter(Boolean)
        .sort()
        .join(',');
    const authorityChanged =
      initial?.agenda === Agenda.COURT &&
      members(initial?.decidingAuthorityMembers) !==
        members(values?.decidingAuthorityMembers);

    if (judgeChanged || authorityChanged) {
      testPrompt({
        key: 'COURT_FILE_ASSIGNMENT_CHANGE_REASON',
        submitCallback: submit,
      });
    } else if (deletedImportantMethods.length) {
      setTimeout(() => {
        testPrompt({
          key: 'WARNING',
          submitCallback: () => submit(),
        });
      }, 0);
    } else {
      props.onClick();
    }
  });

  return <DetailToolbarButton2 {...props} onClick={handleSave} />;
}
